/**
 * Helper function to clean fields based on conditionalDisplay rules
 * Automatically removes fields from form data when their conditional display rules are not met
 * 
 * @param {object} fields - The fields object from form data
 * @param {array} fieldSchema - The schema definition for nested fields
 * @returns {object} - Cleaned fields object with conditionally hidden fields removed
 * 
 * @example
 * // Schema definition:
 * {
 *   name: "coinbase1099Count",
 *   conditionalDisplay: {
 *     field: "exchanges",
 *     operator: "includes",
 *     value: "coinbase"
 *   }
 * }
 * 
 * // Usage:
 * const cleanedFields = cleanConditionalFields(
 *   { exchanges: ["kraken"], coinbase1099Count: 2 },
 *   schemaFields
 * );
 * // Result: { exchanges: ["kraken"] }
 * // coinbase1099Count is removed because "coinbase" is not in exchanges array
 */
export function cleanConditionalFields(fields, fieldSchema) {
  if (!fields || !fieldSchema || !Array.isArray(fieldSchema)) {
    return fields;
  }

  const cleanedFields = { ...fields };

  fieldSchema.forEach(nestedField => {
    const { name, conditionalDisplay } = nestedField;
    
    // Skip if no conditional display or field doesn't exist
    if (!conditionalDisplay || !(name in cleanedFields)) {
      return;
    }

    const { field: targetField, operator, value: targetValue } = conditionalDisplay;
    const currentFieldValue = cleanedFields[targetField];

    // Evaluate condition
    let shouldKeepField = true;
    switch (operator) {
      case "includes":
        shouldKeepField = Array.isArray(currentFieldValue) && currentFieldValue.includes(targetValue);
        break;
      case "equals":
        shouldKeepField = currentFieldValue === targetValue;
        break;
      case "notEquals":
        shouldKeepField = currentFieldValue !== targetValue;
        break;
      default:
        shouldKeepField = true;
    }

    // Remove field if condition not met
    if (!shouldKeepField) {
      delete cleanedFields[name];
    }
  });

  return cleanedFields;
}
